import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ModalWindow } from '~/components';
import { ArrowRightIcon, Users02Icon, XIcon } from '~/icons';
import {
  Body,
  CancelButton,
  ChangeBox,
  ChangeRow,
  Chip,
  Footer,
  Header,
  LegendLabel,
  TypeBlock,
} from './styles';

type UserTypes = 'cliente' | 'mantenedorv2' | 'fabricante' | 'instalador' | 'persona2' | 'administradorDeConta';

export type UserTypeChangeSnapshot = {
  type: UserTypes;
  client?: string;
  unit?: string;
};

interface ConfirmUserTypeChangeModalProps {
  from: UserTypeChangeSnapshot;
  to: UserTypeChangeSnapshot;
  onConfirm: () => void;
  onCancel: () => void;
}

const USER_TYPE_KEYS: Record<UserTypes, string> = {
  cliente: 'cliente',
  mantenedorv2: 'mantenedor',
  fabricante: 'fabricante',
  instalador: 'Instalador',
  persona2: 'personaII',
  administradorDeConta: 'administradorDeConta',
};

export function ConfirmUserTypeChangeModal({
  from,
  to,
  onConfirm,
  onCancel,
}: Readonly<ConfirmUserTypeChangeModalProps>): ReactElement {
  const { t } = useTranslation();

  const clientChanged = !!from.client && !!to.client && from.client !== to.client;
  const unitChanged = !!from.unit && !!to.unit && from.unit !== to.unit;

  return (
    <ModalWindow style={{ maxWidth: '480px' }}>
      <Header>
        <div>
          <Users02Icon />
          {t('confirmarAlteracaoTipoUsuario')}
        </div>
        <button type="button" onClick={onCancel} aria-label="Fechar">
          <XIcon />
        </button>
      </Header>

      <Body>
        <p>{t('textoConfirmarAlteracaoTipo')}</p>

        <ChangeBox>
          <TypeBlock>
            <LegendLabel>{t('atual')}</LegendLabel>
            <span />
            <LegendLabel>{t('novo')}</LegendLabel>
            <Chip variant="current">{t(USER_TYPE_KEYS[from.type])}</Chip>
            <ArrowRightIcon />
            <Chip variant="new">{t(USER_TYPE_KEYS[to.type])}</Chip>
          </TypeBlock>

          {clientChanged && (
            <ChangeRow>
              <strong>
                {t('cliente')}
                :
              </strong>
              <span>{from.client}</span>
              <ArrowRightIcon />
              <span>{to.client}</span>
            </ChangeRow>
          )}
          {unitChanged && (
            <ChangeRow>
              <strong>
                {t('unidade')}
                :
              </strong>
              <span>{from.unit}</span>
              <ArrowRightIcon />
              <span>{to.unit}</span>
            </ChangeRow>
          )}
        </ChangeBox>
      </Body>

      <Footer>
        <CancelButton type="button" onClick={onCancel}>
          {t('cancelar')}
        </CancelButton>
        <Button
          variant="primary"
          style={{
            fontSize: 13, borderRadius: 4, textTransform: 'unset', width: 'auto',
          }}
          onClick={onConfirm}
        >
          {t('confirmar')}
        </Button>
      </Footer>
    </ModalWindow>
  );
}
